import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export function DeckCostDisclaimer() {
  return (
    <div className="bg-gray-50 border-l-4 border-beaver-green p-6 my-8 rounded-r-md shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0 pt-1">
          <i className="fas fa-info-circle text-beaver-green text-xl" aria-hidden="true"></i>
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-bold text-text-dark font-secondary mb-2 uppercase">
            Calgary Deck Repair & Replacement Cost Disclaimer
          </h3>
          <p className="text-sm text-gray-700 font-text leading-relaxed mb-3">
            The repair costs (such as the $500–$1,500 range for isolated surface damage) and full deck replacement estimates provided in this article reflect current Calgary market averages for materials and professional labor. 
          </p>
          <p className="text-sm text-gray-700 font-text leading-relaxed mb-3">
            Actual project costs vary based on ledger board integrity, joist rot, footing stability, and current City of Calgary building code requirements. These figures are for informational planning and do not represent a binding quote.
          </p>
          <p className="text-sm text-text-dark font-text leading-relaxed font-bold">
            A definitive assessment of whether to repair or replace requires a structural inspection. Contact Busy Beaver Construction at <a href="tel:4032865400" className="text-beaver-green hover:underline">(403) 286-5400</a> to book an exact evaluation of your deck's condition.
          </p>
        </div>
      </div>
    </div>
  )
}

export default function DeckRepairVsReplacementBlog() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  
  // Consolidated Schema Data (Article, FAQ, Breadcrumb)
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Calgary Deck Repair vs. Replacement: How to Decide",
            "item": "https://busybeaverconstruction.com/blog/deck-repair-vs-replacement-calgary"
          }
        ]
      },
      {
        "@type": "Article",
        "headline": "Calgary Deck Repair vs. Replacement: How to Decide",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "logo": {
            "@type": "ImageObject",
            "url": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png"
          }
        },
        "datePublished": "2026-06-04",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do I know if my Calgary deck needs to be replaced or just repaired?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Replace if structural members — posts, beams, joists, or the ledger board — show rot or failure at multiple points. If damage is limited to surface boards or a single railing post, repair is usually sufficient. If repair costs would exceed 40–50% of full replacement cost, or if the deck is 15+ years old with recurring issues, replacement is the better long-term investment."
            }
          },
          {
            "@type": "Question",
            "name": "Does replacing a deck in Calgary require a permit?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Any deck higher than 0.6 m (2 ft) above grade requires a City of Calgary building permit, whether you are building new or replacing an existing structure. The standard deck building permit costs approximately $205.92. Calgary's review process typically takes 5–10 business days for a complete application."
            }
          },
          {
            "@type": "Question",
            "name": "How much does deck replacement cost in Calgary in 2025?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A pressure-treated wood deck replacement runs approximately $30–$50 per square foot installed. Composite deck replacements run $55–$85 per square foot. A 200 sq ft replacement in pressure-treated lumber costs approximately $6,000–$10,000. The same footprint in TimberTech composite: approximately $11,000–$17,000. These figures include demolition, footings, framing, decking, and standard railing."
            }
          },
          {
            "@type": "Question",
            "name": "Can I repair just the deck boards without replacing the frame?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, if the frame (joists, beams, posts, ledger) is structurally sound. Probe all framing members with a screwdriver before deciding. If the tool sinks more than 3 mm into any member under light pressure, that member has rot and needs replacement regardless of what the surface boards look like."
            }
          },
          {
            "@type": "Question",
            "name": "What are the most dangerous signs that a deck is unsafe?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The highest-risk indicators are: ledger board pulling away from the house wall (even slightly), posts rotting at the base, railing posts that move when pushed, and visible sagging across the deck surface. Any one of these warrants stopping use of the deck and getting a professional inspection immediately."
            }
          },
          {
            "@type": "Question",
            "name": "How long does a Calgary deck replacement take?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Plan for 4–7 weeks from signing a contract to final inspection. The City of Calgary targets a 7-business-day permit review. Construction for a typical 200–300 sq ft deck takes 8–15 working days, with additional time for concrete curing and inspection scheduling."
            }
          },
          {
            "@type": "Question",
            "name": "Is it cheaper to repair or replace a deck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Repair is always cheaper upfront. The better question is: which is more cost-effective over 10 years? A $2,000 structural repair on a 15-year-old deck may need to be repeated in 3–4 years, and will not prevent eventual failure of remaining aged components. A replacement — though $10,000–$20,000 — delivers 25+ years with a composite product warranty and no recurring structural repair costs."
            }
          }
        ]
      }
    ]
  };

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>Calgary Deck Repair vs. Replacement Guide | Busy Beaver</title>
        <meta name="description" content="Rotting boards, sagging joists, loose railings — know when to repair your Calgary deck and when to replace it entirely. Costs, permits, and contractor tips inside." />
        <meta name="keywords" content="deck repair vs replacement Calgary, Calgary deck replacement cost, when to replace a deck Calgary, Calgary deck permit, deck inspection Calgary, composite vs wood deck Calgary" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/deck-repair-vs-replacement-calgary" />
        <meta property="og:title" content="Calgary Deck Repair vs. Replacement: How to Decide" />
        <meta property="og:description" content="Rotting boards, sagging joists, loose railings — know when to repair your Calgary deck and when to replace it entirely. Costs, permits, and contractor tips inside." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://busybeaverconstruction.com/blog/deck-repair-vs-replacement-calgary" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[450px] md:h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg"
          alt="Homeowner inspecting a deck for structural damage and wood rot in Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-24 text-center max-w-5xl mx-auto px-4">
          <div className="inline-block bg-[#86efac] rounded py-1 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Safety & Maintenance Guide</p>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight italic">
            Calgary Deck Repair vs. Replacement: How to Decide
          </h1>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 relative">
            
            <article className="lg:col-span-8 space-y-6 text-[17px] text-gray-700 leading-relaxed font-text">
              
              <div className="article-introduction">
                <p className="mb-4">
                  When your outdoor living space shows signs of rot, sagging boards, or unstable railings, deciding whether to repair or replace the structure is critical. As a trusted <a href="https://busybeaverconstruction.com/" className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">deck builder in Calgary</a>, we evaluate the underlying frame to determine the safest and most cost-effective path forward.
                </p>

                <p className="mb-4">
                  Isolated damage—such as a few weathered surface boards or a single loose railing post—can typically be repaired for $500 to $1,500. However, if Calgary's severe freeze-thaw cycles have caused moisture damage to the ledger board, structural joists, or support footings, patching the surface is a wasted investment. Once structural rot sets in, a full replacement is mandatory.
                </p>

                <p className="mb-6">
                  This guide provides Calgary homeowners with the exact diagnostic signs that dictate a total replacement, outlines current City of Calgary permit requirements, details local project cost ranges, and explains how to vet a qualified contractor.
                </p>

                <DeckCostDisclaimer />
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                When to Repair Your Calgary Deck vs. Replace It
              </h2>
              <p>
                The repair-or-replace decision comes down to two things: how many structural members are compromised, and how old the deck is.
              </p>

              <p><strong>Repair makes sense when:</strong></p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>Damage is limited to surface decking boards, a single railing post, or cosmetic hardware</li>
                <li>The frame — joists, beams, posts, and ledger — is structurally sound when probed</li>
                <li>The deck is under 10 years old and was built to current Alberta Building Code</li>
                <li>Repair costs are less than 40–50% of what replacement would cost</li>
              </ul>

              <p><strong>Replace when:</strong></p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>Multiple structural members (posts, beams, ledger, joists) show rot or failure</li>
                <li>The ledger board is pulling away from the house at any point</li>
                <li>The deck sways or moves underfoot when loaded</li>
                <li>The deck is 15+ years old and repairs are recurring</li>
                <li>Repair scope would approach 50% or more of replacement cost — at that level, the remaining structure is likely to fail within a few years anyway</li>
              </ul>

              <p>
                A pressure-treated wood deck in Calgary's climate has a realistic lifespan of 10–15 years with proper annual maintenance. Composite decks can last 25–30 years. If your wood deck is already 12–15 years old and showing any structural issues, replacement is almost always the right call financially.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                The Homeowner Inspection: What to Check Before Calling a Contractor
              </h2>
              <p>
                Before getting quotes, do this yourself. A 30-minute walk-around will tell you how serious the situation is.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">The Screwdriver Test</h3>
              <p>
                Take a long screwdriver or awl and push the tip firmly into the wood at each of these locations: post bases, joist ends, stair stringers, ledger board, and any board that looks discoloured or darker than surrounding wood.
              </p>
              <p>
                Healthy wood is hard. You will feel clear resistance. Rotted wood is spongy — the tool will sink more than 3 mm (approximately 1/8 inch) under light pressure, and wood fibres will crumble rather than compress. If this happens at a post base or the ledger, you are not dealing with a surface problem.
              </p>
              <p>
                Rot at post bases is extremely common in Calgary because of freeze-thaw cycles and snow accumulation at grade level. Posts that appear solid from the outside can be completely hollow 150 mm from the bottom.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Connections and Hardware</h3>
              <p>Every load-bearing connection deserves a look. Check for:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li><strong>Ledger attachment:</strong> The ledger board — where the deck frame bolts to your house — must be secured with carriage bolts, not nails. If it is nail-fastened or if you see any separation between the ledger and the house wall, treat it as an urgent safety issue. A detaching ledger is the leading cause of deck collapses.</li>
                <li><strong>Ledger flashing:</strong> Metal flashing must sit above the ledger to direct water away from the connection. Missing or failed flashing will rot the top of the ledger from the inside out, often without visible signs until failure.</li>
                <li><strong>Joist hangers:</strong> Look for rust, missing fasteners, or bent hangers. All hardware should be hot-dipped galvanized or stainless steel. Calgary's freeze-thaw cycles accelerate corrosion on standard steel.</li>
                <li><strong>Post bases:</strong> Posts should be anchored to concrete piers with proper post-base hardware, not just sitting on blocks or embedded in soil.</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Railings and Stairs</h3>
              <p>
                Grip each railing section and apply lateral force. A code-compliant railing post must resist a 200-pound lateral load. If it moves at all, the post is not properly fastened.
              </p>
              <p>
                In Calgary, guardrails on decks between 0.6 m and 1.8 m above grade must be at least 0.9 m high. For decks higher than 1.8 m, the minimum is 1.07 m. Baluster spacing must not exceed 100 mm (4 inches). Any railing post attached only with nails will loosen over time and is not code-compliant — it needs to be bolted through the framing.
              </p>
              <p>
                For stairs: stair handrails are required for any stair with three or more risers, at a height of 865–965 mm. Check that stringers are not rotted at the base where they meet grade.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">The Walk Test</h3>
              <p>
                Walk the entire deck slowly. Note any boards that feel springy, bounce underfoot, or sound hollow. A springy feel across a section — not just a single board — often means the joist beneath is compromised. That requires investigation before the decking is replaced, not after.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Calgary Deck Permits: What Repair and Replacement Projects Require
              </h2>
              <p>
                Understanding permit requirements before you start work saves you from expensive after-the-fact compliance problems.
              </p>
              <p><strong>Building permit required</strong> if:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>Any part of an uncovered deck surface is higher than 0.6 m (24 inches) above grade</li>
                <li>You are rebuilding, extending, or structurally altering a deck that exceeds that height</li>
                <li>You are adding or replacing stairs on an existing deck</li>
                <li>The deck has any form of roof or cover structure</li>
              </ul>

              <p><strong>No building permit required</strong> if:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>The entire uncovered deck stays at or below 0.6 m at every point</li>
                <li>You are replacing surface boards only, without touching structural members, on a deck that does not require a permit</li>
              </ul>

              <p>
                <strong>Development permit may also be required</strong> if the deck is in a front yard, or if it encroaches within 1.2 m of a side property line or 6 m of a rear property line. Corner lots and irregular lots are more likely to trigger this requirement.
              </p>

              <p>
                The City of Calgary's standard deck building permit fee is approximately $205.92. A development permit, if required, costs $550 and adds approximately 10–12 weeks to the process. The standard building permit review target is 7 business days for a complete application.
              </p>
              <p>
                One important note: replacing structural members (ledger, posts, beams) on an existing deck above 0.6 m requires a permit, even if you are not changing the footprint or height. "Structural repair" and "cosmetic repair" are treated differently by the City.
              </p>
              <p>
                If you are digging new footings as part of a replacement, contact Alberta's 811 Click Before You Dig service before any excavation to locate buried utilities. Calgary's frost line is approximately 1.2 m deep — footings must extend below this to prevent frost heave.
              </p>
              <p>
                For a full breakdown of permit requirements and fees, see our <a href="/blog/4" onClick={(e) => handleNavigation(e, '/blog/4')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">Calgary deck permit guide</a>.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                How Much Does Deck Repair vs. Replacement Cost in Calgary?
              </h2>
              <p>
                Cost is almost always the deciding factor in the repair-or-replace conversation. Here are current Calgary market ranges.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Repair Costs</h3>
              <p>
                Minor repairs — replacing a few boards, tightening hardware, resealing — can be done for $300–$800. More involved professional repair work, such as replacing rotten joists, adding bolted railing posts, or fixing a ledger connection, typically runs $800–$2,500 depending on scope.
              </p>
              <p>Railing replacement or upgrade: $500–$4,000 depending on material and linear footage.</p>
              <p>Deck board resurfacing (boards only, no structural work): $10–$35 per square foot installed.</p>
              <p>
                The problem with repair costs is they do not address the underlying age of the structure. Spending $2,000 to replace joists on a 16-year-old deck may buy you 3–5 more years before the posts or ledger require the same attention.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Replacement Costs</h3>
              <p>Full deck replacements in Calgary currently run:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li><strong>Pressure-treated wood, basic layout:</strong> $30–$50 per square foot installed. A 200 sq ft deck: approximately $6,000–$10,000.</li>
                <li><strong>Cedar:</strong> $40–$70 per square foot installed.</li>
                <li><strong>Composite (TimberTech, Trex, Fiberon):</strong> $55–$85 per square foot installed. A 200 sq ft deck: approximately $11,000–$17,000.</li>
              </ul>
              <p>
                These ranges include demolition of the existing deck, new concrete footings, full framing, decking, and standard railing. Multi-level designs, custom railing styles (glass, cable), built-in features, and stairs add to the total.
              </p>
              <p>
                Permit fees add approximately $200–$750 depending on whether a development permit is required. Disposal of demolished material is typically included in contractor quotes but confirm this in writing.
              </p>
              <p>
                For a detailed breakdown of what drives these numbers, see our <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">Calgary deck cost guide</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">The ROI Calculation</h3>
              <p>
                Composite decks cost 2–3 times more than pressure-treated wood upfront. Over 20 years, the math often reverses. Pressure-treated wood requires annual maintenance — cleaning, staining, sealing — plus periodic repairs. Composite requires occasional cleaning. The 25-year product warranties on premium composite decking (TimberTech AZEK, for example) are transferable to new homeowners and have real value at resale.
              </p>
              <p>
                If you are planning to sell within 5 years: a code-compliant, visually clean deck — even pressure-treated — adds perceived value and removes a potential red flag during home inspection. An unpermitted or visibly unsafe deck is a negotiation liability.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Material Failure Modes: What Goes Wrong and When
              </h2>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Pressure-Treated Wood</h3>
              <p>
                The most common failure points are: post bases rotting at grade contact, top-of-ledger rot from failed flashing, joist ends at hangers corroding and failing, and surface boards checking and splitting from freeze-thaw cycles.
              </p>
              <p>
                ACQ-treated lumber (the standard today) contains copper compounds that accelerate corrosion on standard galvanized hardware. All fasteners and connectors used with ACQ lumber must be rated for ACQ contact — hot-dipped galvanized or stainless steel. This is a common mistake in older Calgary decks.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Composite Decking</h3>
              <p>
                Composite boards do not rot, but they have their own failure modes. Lower-quality composites can sag between joists if framing spacing is too wide — most manufacturers specify maximum joist spacing in their installation guides. Surface mould and mildew is common in shaded, north-facing areas; it is cosmetic and does not compromise the board structurally, but it requires cleaning.
              </p>
              <p>
                Composite framing is still typically pressure-treated wood — the failure modes for the structure are the same as any wood deck.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Aluminum and Metal Components</h3>
              <p>
                Aluminum railings are extremely durable but can corrode galvanically when in direct contact with ACQ lumber or dissimilar metals. Use appropriate isolation tape or hardware between aluminum components and treated wood framing. Steel components in painted or powder-coated finishes should be inspected annually for coating failures that expose base metal to moisture.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Busy Beaver Project: Full Composite Deck Replacement in SW Calgary
              </h2>
              <p>
                A homeowner in the Bridlewood area contacted us after an annual inspection found rot at three support posts and visible ledger separation at the house wall. The deck was 17 years old, pressure-treated, approximately 220 sq ft with a single stair set.
              </p>
              <p>
                We performed a probe test on all structural members before quoting. Ledger rot extended approximately 600 mm in from the house connection. Two of four posts were hollow at the base. The joist ends at two hangers had failed.
              </p>
              <p>
                Our recommendation: full replacement. Repairing the ledger and posts alone would have cost approximately $3,500–$4,500 — roughly 25–30% of replacement cost — and would have left the remaining 13-year-old framing in place with no structural warranty.
              </p>
              <p>
                The replacement project included full demolition and disposal, new concrete piers to 1.4 m depth, pressure-treated framing, TimberTech AZEK composite decking in Weathered Teak, and aluminum railing with glass infill panels. Permit was applied for and approved in 6 business days. Build time was 9 working days.
              </p>
              <p>
                Total project cost: approximately $21,000. The homeowner received a 25-year TimberTech product warranty, a 2-year Busy Beaver workmanship warranty, and a City of Calgary-approved permit and inspection record.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                How to Choose a Calgary Deck Contractor for Repair or Replacement
              </h2>
              <p>
                Whether you are repairing or replacing, the contractor you hire determines whether the work is safe, code-compliant, and properly warranted.
              </p>
              <p><strong>Questions to ask every contractor before signing:</strong></p>
              <p>
                <strong>Are you licensed and insured in Alberta?</strong> Ask for proof of liability insurance and Workers' Compensation Board (WCB) coverage. You are liable for on-site injuries if the contractor is uninsured.
              </p>
              <p>
                <strong>Will you handle the building permit?</strong> In Calgary, the contractor typically applies for the permit as the work applicant. A contractor who suggests skipping the permit or tells you one is not needed for above-grade structural work is a red flag — not a convenience.
              </p>
              <p>
                <strong>How many deck projects have you completed in Calgary in the last two years?</strong> Local experience matters. Calgary's frost depth, soil conditions, and City inspection process are specific. A contractor who works primarily in other jurisdictions will be less familiar with what passes inspection here.
              </p>
              <p>
                <strong>Can you provide three recent Calgary references?</strong> Follow up on them. Ask specifically: did the project come in on budget, was the permit handled properly, and did the work pass inspection on the first review.
              </p>
              <p>
                <strong>What warranty do you offer on workmanship?</strong> Standard is 1–2 years. For composite decking, verify that the contractor is a certified installer for the brand — this is required to validate manufacturer warranties. Busy Beaver Construction is a TimberTech Platinum Pro Builder, which is the highest installer certification level.
              </p>
              <p>
                <strong>What is the payment schedule?</strong> A reasonable schedule: 10–20% deposit, a progress payment at framing completion, final payment on completion and inspection sign-off. Never pay in full before the work starts.
              </p>
              <p>
                <strong>Red flags:</strong> Very low bids without written itemization of scope. Requests for large cash deposits. Reluctance to provide a written contract. Claims that permits are unnecessary. No local references.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Sample Project Timeline: Calgary Deck Replacement
              </h2>
              <p>
                This is a realistic timeline for a straightforward deck replacement (200–300 sq ft, no unusual site conditions):
              </p>

              <div className="overflow-x-auto my-6 shadow-sm rounded border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-sm md:text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-3 md:p-4 font-bold font-secondary">Stage</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Contractor consultation and quote</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">3–7 days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Permit application submission</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Day 1 after signing contract</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">City of Calgary permit review</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">5–10 business days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Demolition and disposal</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">1–2 days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Footing excavation and pour</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">1–2 days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Concrete cure time</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">3–7 days (weather-dependent)</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Framing</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">2–3 days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Decking installation</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">2–3 days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Railing and stairs</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">2–3 days</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">City inspection</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">1–3 days after request</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-medium text-gray-900">Final clean-up and walkthrough</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">1 day</td>
                    </tr>
                    <tr className="bg-gray-100 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Total elapsed time (typical)</td>
                      <td className="p-3 md:p-4 font-bold text-gray-900">4–7 weeks</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p>
                Weather is the primary variable in Calgary. Spring projects can be delayed by late frost or wet ground conditions. Late summer and fall are generally the most reliable windows for on-schedule completion. If a development permit is required, add 10–12 weeks before construction can begin.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Repair vs. Replacement Comparison
              </h2>

              <div className="overflow-x-auto my-6 shadow-sm rounded border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-sm md:text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-3 md:p-4 font-bold font-secondary">Factor</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Repair</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Replace</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Upfront cost</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">$300–$2,500 typical</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">High ($6,000–$25,000+ depending on size and material)</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Safety outcome</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Moderate — hidden damage may remain</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Best — new structure built to current code</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Lifespan gained</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">3–10 years depending on what is fixed</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">15–30+ years depending on material</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Permit required</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Only if structural members or height is affected</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Yes, for any deck above 0.6 m</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Disruption</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Minimal — usually days</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Significant — 4–7 weeks total</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Best for</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Surface damage, isolated post/railing issues, deck under 10 years old</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Multiple structural failures, deck 15+ years old, repair cost approaching 50% of replacement</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Frequently Asked Questions: Calgary Deck Repair and Replacement
              </h2>
              
              <div className="space-y-4">
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How do I know if my Calgary deck needs to be replaced or just repaired?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Replace if structural members — posts, beams, joists, or the ledger board — show rot or failure at multiple points. If damage is limited to surface boards or a single railing post, repair is usually sufficient. If repair costs would exceed 40–50% of full replacement cost, or if the deck is 15+ years old with recurring issues, replacement is the better long-term investment.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Does replacing a deck in Calgary require a permit?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Yes. Any deck higher than 0.6 m (2 ft) above grade requires a City of Calgary building permit, whether you are building new or replacing an existing structure. The standard deck building permit costs approximately $205.92. Calgary's review process typically takes 5–10 business days for a complete application.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How much does deck replacement cost in Calgary in 2025?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    A pressure-treated wood deck replacement runs approximately $30–$50 per square foot installed. Composite deck replacements run $55–$85 per square foot. A 200 sq ft replacement in pressure-treated lumber costs approximately $6,000–$10,000. The same footprint in TimberTech composite: approximately $11,000–$17,000. These figures include demolition, footings, framing, decking, and standard railing.
                  </div>
                </details>
                
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Can I repair just the deck boards without replacing the frame?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Yes, if the frame (joists, beams, posts, ledger) is structurally sound. Probe all framing members with a screwdriver before deciding. If the tool sinks more than 3 mm into any member under light pressure, that member has rot and needs replacement regardless of what the surface boards look like.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">What are the most dangerous signs that a deck is unsafe?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    The highest-risk indicators are: ledger board pulling away from the house wall (even slightly), posts rotting at the base, railing posts that move when pushed, and visible sagging across the deck surface. Any one of these warrants stopping use of the deck and getting a professional inspection immediately.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How long does a Calgary deck replacement take?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Plan for 4–7 weeks from signing a contract to final inspection. The City of Calgary targets a 7-business-day permit review. Construction for a typical 200–300 sq ft deck takes 8–15 working days, with additional time for concrete curing and inspection scheduling.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Is it cheaper to repair or replace a deck?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Repair is always cheaper upfront. The better question is: which is more cost-effective over 10 years? A $2,000 structural repair on a 15-year-old deck may need to be repeated in 3–4 years, and will not prevent eventual failure of remaining aged components. A replacement — though $10,000–$20,000 — delivers 25+ years with a composite product warranty and no recurring structural repair costs.
                  </div>
                </details>
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Homeowner Safety Checklist
              </h2>
              <p>
                Use this checklist for an annual spring inspection or before calling a contractor for quotes.
              </p>
              
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li><strong>Probe posts and ledger</strong> with a screwdriver at base contact points. Any sinking more than 3 mm indicates rot.</li>
                <li><strong>Check ledger flashing</strong> above the deck-to-house connection. Missing flashing is a high-risk defect.</li>
                <li><strong>Verify ledger fasteners</strong> are carriage bolts, not nails. Look for any separation between the ledger and the house wall.</li>
                <li><strong>Test all railing sections</strong> by applying lateral force. Posts must not move.</li>
                <li><strong>Confirm railing height:</strong> 0.9 m minimum for decks 0.6–1.8 m above grade; 1.07 m for decks above 1.8 m.</li>
                <li><strong>Confirm baluster spacing</strong> does not exceed 100 mm (4 inches).</li>
                <li><strong>Walk the deck surface</strong> slowly and note any springy sections or hollow sounds.</li>
                <li><strong>Inspect joist hangers</strong> for rust, missing fasteners, or distortion.</li>
                <li><strong>Check stair stringers</strong> at the base for rot. Probe thoroughly.</li>
                <li><strong>Confirm footings</strong> are on concrete piers, not soil-contact blocks. Look for frost heave or shifted posts.</li>
                <li><strong>Call 811</strong> before any excavation for new footings.</li>
                <li><strong>Verify permit status:</strong> if your deck is above 0.6 m and was not permitted, consult the City of Calgary before doing any work.</li>
              </ul>
              
              <p>
                When in doubt about any structural finding, stop using the deck and get a professional assessment before proceeding. If you determine that you need a full replacement rather than a repair, check out our guide on <a href="/blog/best-time-to-build-deck-calgary" onClick={(e) => handleNavigation(e, '/blog/best-time-to-build-deck-calgary')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">when to schedule your deck replacement</a> to understand Calgary build season wait times.
              </p>

              <div className="mt-16 mb-8 border-t border-gray-200 pt-12">
                <h2 className="text-3xl font-bold text-[#0f172a] mb-8 font-secondary tracking-tight">
                  Ready to upgrade your outdoor space?
                </h2>
                <p className="mb-6">
                  If your deck inspection has raised questions, <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">request a free on-site estimate</a> from Busy Beaver Construction. We have been building and replacing decks in Calgary since 1999 and handle the full permit process on every project. You can also use our <a href="/estimate" onClick={(e) => handleNavigation(e, '/estimate')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">online deck cost calculator</a> to get a preliminary budget range before booking a site visit.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="/appointment" 
                    onClick={(e) => handleNavigation(e, '/appointment')}
                    className="inline-flex justify-center items-center px-8 py-4 bg-[#86efac] text-black font-bold text-[16px] rounded shadow hover:brightness-95 transition-all duration-300 font-accent tracking-wide"
                  >
                    CONTACT US TODAY
                  </a>
                  <a 
                    href="/estimate" 
                    onClick={(e) => handleNavigation(e, '/estimate')}
                    className="inline-flex justify-center items-center px-8 py-4 bg-[#0f172a] text-white font-bold text-[16px] rounded shadow hover:bg-black transition-all duration-300 font-accent tracking-wide"
                  >
                    CALCULATE YOUR PROJECT
                  </a>
                </div>
              </div>

            </article>
            
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit mt-8 lg:mt-6">
              <div className="bg-white rounded shadow-sm border border-gray-100 pb-6">
                <h3 className="text-[22px] font-bold mb-4 font-secondary text-[#0f172a] px-6 pt-6 tracking-tight">Project Inspiration</h3>
                
                <div className="grid grid-cols-1 gap-5 max-h-[850px] overflow-y-auto px-6 custom-scrollbar">
                  {[
                    "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
                    "/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg",
                    "/assets/images/Pergola/Modern-Pergola-design-for-outdoor-seating-area.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
                    "/assets/images/Other/Living Spaces/Custom-Outdoor-Kitchen-in-Cedar.jpg"
                  ].map((src, index) => (
                    <div key={index} className="overflow-hidden h-[220px] group cursor-pointer shadow-sm border-2 border-white ring-1 ring-gray-100">
                      <img 
                        src={src}
                        alt={`Inspiration gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 loading-lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  )
}